import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface ProjekttervezesSor {
  id: number;
  munkaszamId: number;
  sortOrder: number;
  haladas: number | null;
  isArchived: boolean;
  munkaszam: string;
  munkaTargya: string;
  ugyintezoNev: string;
  projektKezdete: string | null;
  tenylegesProjektKezdete: string | null;
  tervezettBefejezes: string | null;
  tenylegesBefejezes: string | null;
  hatralevoMunkanapok: number | null;
}

export interface ProjekttervezesMunkaszamOption {
  id: number;
  munkaszam: string;
  munkaTargya: string;
}

export interface UpdateProjekttervezesPayload {
  haladas?: number | null;
  tenylegesProjektKezdete?: string | null;
  tenylegesBefejezes?: string | null;
}

export interface GetProjekttervezesFilters {
  includeArchived?: boolean;
  projektKezdeteTol?: string | null;
  projektKezdeteIg?: string | null;
  munkaszamQuery?: string;
}

export type ProjekttervezesFeladatStatus = 'inProgress' | 'stuck' | 'canceled' | 'finished';

export interface ProjekttervezesFeladatFelelos {
  userId: string;
  userName: string;
}

export interface ProjekttervezesFeladat {
  id: number;
  tetelId: number;
  csoportId: number | null;
  sortOrder: number;
  nev: string;
  leiras: string;
  kezdetiDatum: string | null;
  befejezesDatum: string | null;
  status: ProjekttervezesFeladatStatus;
  szin: string;
  felelosok: ProjekttervezesFeladatFelelos[];
}

export interface CreateProjekttervezesFeladatPayload {
  csoportId?: number | null;
  nev: string;
  leiras?: string;
  kezdetiDatum?: string | null;
  befejezesDatum?: string | null;
  status?: ProjekttervezesFeladatStatus;
  szin?: string;
  userIds?: string[];
}

export interface UpdateProjekttervezesFeladatPayload extends CreateProjekttervezesFeladatPayload {}

export interface UpdateProjekttervezesFeladatDatesPayload {
  kezdetiDatum?: string | null;
  befejezesDatum?: string | null;
}

export interface ProjekttervezesCsoport {
  id: number;
  tetelId: number;
  parentCsoportId: number | null;
  sortOrder: number;
  nev: string;
  leiras: string;
  szin: string;
}

export interface CreateProjekttervezesCsoportPayload {
  parentCsoportId?: number | null;
  nev: string;
  leiras?: string;
  szin?: string;
}

export interface UpdateProjekttervezesCsoportPayload extends CreateProjekttervezesCsoportPayload {}

export interface ProjekttervezesItemOrderEntry {
  kind: 'group' | 'task';
  id: number;
}

export interface ReorderProjekttervezesItemsPayload {
  parentCsoportId?: number | null;
  items: ProjekttervezesItemOrderEntry[];
}

export interface ProjekttervezesFeladatSajat {
  tetelId: number;
  feladatId: number;
  munkaszam: string;
  munkaTargya: string;
  feladatNev: string;
  hatarido: string | null;
  csoportId: number | null;
  csoportPath: number[];
}

export interface ProjekttervezesFocus {
  tetelId: number;
  feladatId: number;
  csoportPath: number[];
}

export const PROJEKTTERVEZES_FELADAT_STATUS_LABELS: Record<ProjekttervezesFeladatStatus, string> = {
  inProgress: 'Folyamatban',
  stuck: 'Elakadt',
  canceled: 'Megszakítva',
  finished: 'Kész',
};

export const PROJEKTTERVEZES_FELADAT_STATUS_OPTIONS: ProjekttervezesFeladatStatus[] = [
  'inProgress',
  'stuck',
  'canceled',
  'finished',
];

export const PROJEKTTERVEZES_FELADAT_COLOR_PRESETS = [
  '#64b5f6',
  '#4caf50',
  '#ff9800',
  '#e53935',
  '#9c27b0',
  '#00bcd4',
  '#795548',
  '#607d8b',
  '#f06292',
  '#aed581',
];

const mapFeladat = (item: any): ProjekttervezesFeladat => ({
  id: item.id,
  tetelId: item.tetelId,
  csoportId: item.csoportId ?? null,
  sortOrder: item.sortOrder,
  nev: item.nev ?? '',
  leiras: item.leiras ?? '',
  kezdetiDatum: item.kezdetiDatum ?? null,
  befejezesDatum: item.befejezesDatum ?? null,
  status: (item.status ?? 'inProgress') as ProjekttervezesFeladatStatus,
  szin: item.szin ?? '#64b5f6',
  felelosok: Array.isArray(item.felelosok)
    ? item.felelosok.map((felelos: any) => ({
        userId: String(felelos.userId ?? ''),
        userName: String(felelos.userName ?? ''),
      }))
    : [],
});

const mapCsoport = (item: any): ProjekttervezesCsoport => ({
  id: item.id,
  tetelId: item.tetelId,
  parentCsoportId: item.parentCsoportId ?? null,
  sortOrder: item.sortOrder,
  nev: item.nev ?? '',
  leiras: item.leiras ?? '',
  szin: item.szin ?? '#64b5f6',
});

const mapRow = (item: any): ProjekttervezesSor => ({
  id: item.id,
  munkaszamId: item.munkaszamId,
  sortOrder: item.sortOrder,
  haladas: item.haladas ?? null,
  isArchived: Boolean(item.isArchived),
  munkaszam: item.munkaszam ?? '',
  munkaTargya: item.munkaTargya ?? '',
  ugyintezoNev: item.ugyintezoNev ?? '',
  projektKezdete: item.projektKezdete ?? null,
  tenylegesProjektKezdete: item.tenylegesProjektKezdete ?? null,
  tervezettBefejezes: item.tervezettBefejezes ?? null,
  tenylegesBefejezes: item.tenylegesBefejezes ?? null,
  hatralevoMunkanapok: item.hatralevoMunkanapok ?? null,
});

class ProjekttervezesService {
  async getAll(filters?: GetProjekttervezesFilters): Promise<ProjekttervezesSor[]> {
    const response = await axios.get(`${API_BASE_URL}/Projekttervezes`, {
      params: {
        includeArchived: filters?.includeArchived ?? false,
        projektKezdeteTol: filters?.projektKezdeteTol || undefined,
        projektKezdeteIg: filters?.projektKezdeteIg || undefined,
        munkaszamQuery: filters?.munkaszamQuery?.trim() || undefined,
      },
      withCredentials: true,
    });
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map(mapRow);
  }

  async getAvailableMunkaszamok(): Promise<ProjekttervezesMunkaszamOption[]> {
    const response = await axios.get(`${API_BASE_URL}/Projekttervezes/available-munkaszamok`, {
      withCredentials: true,
    });
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map((item: any) => ({
      id: item.id,
      munkaszam: item.munkaszam ?? '',
      munkaTargya: item.munkaTargya ?? '',
    }));
  }

  async add(munkaszamId: number): Promise<ProjekttervezesSor> {
    const response = await axios.post(
      `${API_BASE_URL}/Projekttervezes`,
      { munkaszamId },
      { withCredentials: true },
    );
    return mapRow(response.data);
  }

  async remove(tetelId: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/Projekttervezes/${tetelId}`, {
      withCredentials: true,
    });
  }

  async reorder(tetelIds: number[]): Promise<void> {
    await axios.put(
      `${API_BASE_URL}/Projekttervezes/reorder`,
      { tetelIds },
      { withCredentials: true },
    );
  }

  async update(tetelId: number, payload: UpdateProjekttervezesPayload): Promise<ProjekttervezesSor> {
    const response = await axios.patch(
      `${API_BASE_URL}/Projekttervezes/${tetelId}`,
      payload,
      { withCredentials: true },
    );
    return mapRow(response.data);
  }

  async setArchived(tetelId: number, isArchived: boolean): Promise<ProjekttervezesSor> {
    const response = await axios.patch(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/archive`,
      { isArchived },
      { withCredentials: true },
    );
    return mapRow(response.data);
  }

  async getFeladatok(tetelId: number): Promise<ProjekttervezesFeladat[]> {
    const response = await axios.get(`${API_BASE_URL}/Projekttervezes/${tetelId}/feladatok`, {
      withCredentials: true,
    });
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map(mapFeladat);
  }

  async getCsoportok(tetelId: number): Promise<ProjekttervezesCsoport[]> {
    const response = await axios.get(`${API_BASE_URL}/Projekttervezes/${tetelId}/csoportok`, {
      withCredentials: true,
    });
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map(mapCsoport);
  }

  async addFeladat(tetelId: number, payload: CreateProjekttervezesFeladatPayload): Promise<ProjekttervezesFeladat> {
    const response = await axios.post(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/feladatok`,
      payload,
      { withCredentials: true },
    );
    return mapFeladat(response.data);
  }

  async addCsoport(tetelId: number, payload: CreateProjekttervezesCsoportPayload): Promise<ProjekttervezesCsoport> {
    const response = await axios.post(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/csoportok`,
      payload,
      { withCredentials: true },
    );
    return mapCsoport(response.data);
  }

  async updateCsoport(
    tetelId: number,
    csoportId: number,
    payload: UpdateProjekttervezesCsoportPayload,
  ): Promise<ProjekttervezesCsoport> {
    const response = await axios.put(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/csoportok/${csoportId}`,
      payload,
      { withCredentials: true },
    );
    return mapCsoport(response.data);
  }

  async updateFeladat(
    tetelId: number,
    feladatId: number,
    payload: UpdateProjekttervezesFeladatPayload,
  ): Promise<ProjekttervezesFeladat> {
    const response = await axios.put(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/feladatok/${feladatId}`,
      payload,
      { withCredentials: true },
    );
    return mapFeladat(response.data);
  }

  async updateFeladatDates(
    tetelId: number,
    feladatId: number,
    payload: UpdateProjekttervezesFeladatDatesPayload,
  ): Promise<ProjekttervezesFeladat> {
    const response = await axios.patch(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/feladatok/${feladatId}/dates`,
      payload,
      { withCredentials: true },
    );
    return mapFeladat(response.data);
  }

  async removeFeladat(tetelId: number, feladatId: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/Projekttervezes/${tetelId}/feladatok/${feladatId}`, {
      withCredentials: true,
    });
  }

  async removeCsoport(tetelId: number, csoportId: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/Projekttervezes/${tetelId}/csoportok/${csoportId}`, {
      withCredentials: true,
    });
  }

  async reorderFeladatok(tetelId: number, feladatIds: number[]): Promise<void> {
    await axios.put(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/feladatok/reorder`,
      { feladatIds },
      { withCredentials: true },
    );
  }

  async reorderItems(tetelId: number, payload: ReorderProjekttervezesItemsPayload): Promise<void> {
    await axios.put(
      `${API_BASE_URL}/Projekttervezes/${tetelId}/items/reorder`,
      payload,
      { withCredentials: true },
    );
  }

  async getFeladataim(): Promise<ProjekttervezesFeladatSajat[]> {
    const response = await axios.get(`${API_BASE_URL}/Projekttervezes/feladataim`, {
      withCredentials: true,
    });
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map((item: any) => ({
      tetelId: item.tetelId,
      feladatId: item.feladatId,
      munkaszam: item.munkaszam ?? '',
      munkaTargya: item.munkaTargya ?? '',
      feladatNev: item.feladatNev ?? '',
      hatarido: item.hatarido ?? null,
      csoportId: item.csoportId ?? null,
      csoportPath: Array.isArray(item.csoportPath) ? item.csoportPath.map(Number) : [],
    }));
  }
}

export const projekttervezesService = new ProjekttervezesService();
